import { useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import {
  useImages,
  useUploadImage,
  useDeleteImage,
  useBatchDeleteImages,
  type ImageDto,
} from '../api/queries/media';
import { AppImage } from '../components/common/AppImage';
import { ImageLightbox } from '../components/common/ImageLightbox';
import { ConfirmDialog } from '../components/common/ConfirmDialog';

export function MediaLibraryRoute() {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useImages(search);
  const upload = useUploadImage();
  const deleteImage = useDeleteImage();
  const batchDelete = useBatchDeleteImages();

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const lightboxOpen = lightboxIndex >= 0;
  const images = data ?? [];

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<ImageDto | null>(null);

  // Multi-select state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(images.map((img) => img.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      await upload.mutateAsync(file);
    }
    event.target.value = '';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Media library
        </Typography>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {selectMode ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {selected.size} selected
            </Typography>
            <Tooltip title="Select all">
              <IconButton onClick={selectAll} size="small">
                <SelectAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deselect all">
              <IconButton onClick={deselectAll} size="small">
                <DeselectIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="error"
              disabled={selected.size === 0 || batchDelete.isPending}
              onClick={() => setBatchDeleteConfirm(true)}
              startIcon={
                batchDelete.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {batchDelete.isPending
                ? 'Deleting...'
                : `Delete (${selected.size})`}
            </Button>
            <Button variant="outlined" onClick={exitSelectMode}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={() => setSelectMode(true)}
              disabled={images.length === 0}
            >
              Select
            </Button>
            <Button
              variant="contained"
              component="label"
              disabled={upload.isPending}
            >
              Upload
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />
            </Button>
          </>
        )}
      </Stack>

      {isLoading && <CircularProgress />}

      <ImageList cols={4} gap={12}>
        {images.map((image, index) => (
          <ImageListItem
            key={image.id}
            sx={{
              position: 'relative',
              outline: selected.has(image.id) ? '3px solid' : 'none',
              outlineColor: 'primary.main',
              borderRadius: 1,
            }}
          >
            {selectMode && (
              <Checkbox
                checked={selected.has(image.id)}
                onChange={() => toggleSelect(image.id)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  zIndex: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
                size="small"
              />
            )}
            <AppImage
              src={image.thumbnailUrl ?? image.url}
              alt={image.fileName}
              draggable={!selectMode}
              onDragStart={
                selectMode
                  ? undefined
                  : (e) =>
                      e.dataTransfer.setData('application/x-image-id', image.id)
              }
              onClick={() => {
                if (selectMode) {
                  toggleSelect(image.id);
                } else {
                  setLightboxIndex(index);
                }
              }}
              aspectRatio="1/1"
              objectPosition="top center"
              sx={{ cursor: selectMode ? 'pointer' : 'pointer' }}
            />
            <ImageListItemBar
              title={image.fileName}
              subtitle={`${Math.round(image.size / 1024)} KB`}
              actionIcon={
                !selectMode ? (
                  <Tooltip title="Delete image">
                    <IconButton
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(image);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }
            />
          </ImageListItem>
        ))}
        {images.length === 0 && !isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No images yet. Upload a file or generate one from a paragraph.
          </Typography>
        )}
      </ImageList>

      {/* Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        images={images.map((img) => ({
          src: img.url,
          alt: img.fileName,
          label: img.fileName,
        }))}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
      />

      {/* Single delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete image"
        message={`Are you sure you want to delete "${deleteTarget?.fileName}"? This action cannot be undone.`}
        onConfirm={() => {
          if (deleteTarget) {
            deleteImage.mutate(deleteTarget.id);
            if (lightboxIndex >= 0) setLightboxIndex(-1);
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Batch delete confirmation */}
      <ConfirmDialog
        open={batchDeleteConfirm}
        title="Delete selected images"
        message={`Are you sure you want to delete ${selected.size} image${selected.size === 1 ? '' : 's'}? This action cannot be undone.`}
        confirmLabel={`Delete ${selected.size}`}
        confirmColor="error"
        onConfirm={async () => {
          setBatchDeleteConfirm(false);
          await batchDelete.mutateAsync(Array.from(selected));
          exitSelectMode();
        }}
        onCancel={() => setBatchDeleteConfirm(false)}
      />
    </Paper>
  );
}
